import { useCallback, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Container, Header } from "components/Page"
import { Route, Switch, useHistory, useParams } from "react-router-dom"
import {
  activateMarketAgency,
  deactivateAgency,
  deleteAgency,
  fetchAgency,
} from "api/agencies"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation, useQuery } from "react-query"
import { styledTabClasses, styledTabsProps } from "components/Tabs"

import { ApiError } from "api/http/utils"
import Badge from "components/Badge"
import Breadcrumbs from "components/Breadcrumbs"
import { ReactComponent as CheckIcon } from "assets/check.svg"
import Clients from "./_Clients"
import { CodeSublabel } from "components/HeaderSublabel/CodeSublabel"
import { ColorMap } from "utils/color"
import Commissions from "../../Commissions"
import Companies from "./Companies"
import { ReactComponent as Deactivate } from "assets/icon-deactivate.svg"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import Documents from "./_Documents"
import Fees from "../../Fees"
import GlobalMediaTypes from "routes/Base/Admin/GlobalMediaTypes"
import MoreActions from "components/MoreActions"
import Profile from "./Profile"
import { SnackBar } from "components/SnackBar"
import { Surface } from "components/Surface"
import { TabLink } from "components/Link"
import Tabs from "@material-ui/core/Tabs"
import { Team } from "./Team"
import Vendors from "./_Vendors"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

const styles = {
  surface: css`
    padding: var(--spacing-xxl-2) 0px 0px;
  `,
  btn: css`
    > span {
      text-transform: capitalize;
    }
  `,
  main: css`
    flex: 1;
    display: flex;
    flex-direction: column;
  `,
  wrapper: css`
    padding: var(--spacing-xl) var(--spacing-m) var(--spacing-m);
  `,
  documents: css`
    display: flex;
    grid-column-gap: var(--spacing-xxl-2);
  `,
}

const tabs = [
  "profile",
  "companies",
  "clients",
  "vendors",
  "team",
  "media-types",
  "fees",
  "commissions",
  "documents",
]

function MarketAgencyDetails() {
  const { t } = useI18n()
  const history = useHistory()
  const { id, tab = "profile" } = useParams<{ id: string; tab: string }>()
  const { data: agency } = useQuery(["agency-details", id], fetchAgency, {
    enabled: !!id,
  })
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errors, setErrors] = useState<Record<string, any>>()
  const { hasAuthority } = useAuthenticated()

  const [_delete] = useMutation(deleteAgency, {
    onSuccess: async () => {
      setModalInfo(undefined)
      setShowSuccess(true)
      queryCache.invalidateQueries("market-agencies")
      history.push("/market-agencies")
    },
  })

  const [activate] = useMutation(activateMarketAgency, {
    onSuccess: async () => {
      setModalInfo(undefined)
      setShowSuccess(true)
      queryCache.invalidateQueries("agency-details")
      history.push(`/market-agencies/${id}/${tab}`)
    },
    onError: (e: any) => {
      const error = e as ApiError
      setErrors(error)
      setModalInfo(undefined)
      setShowError(true)
    },
  })

  const [deactivate] = useMutation(deactivateAgency, {
    onSuccess: async () => {
      setModalInfo(undefined)
      setShowSuccess(true)
      queryCache.invalidateQueries("agency-details")
      history.push(`/market-agencies/${id}/${tab}`)
    },
  })

  const onDelete = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("market-admin.agencies.list.delete.title"),
        type: "primary_destructive",
        description: t("market-admin.agencies.list.delete.description"),
        submitLabel: t("market-admin.agencies.list.delete.action"),
        onSubmit: async () => await _delete(id),
      })
    },
    [_delete, t],
  )

  const onActivate = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("agency.modal.activate.title"),
        description: t("agency.modal.activate.description"),
        submitLabel: t("modal.label.activate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await activate({ id }),
      })
    },
    [activate, t],
  )

  const onDeactivate = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("agency.modal.deactivate.title"),
        type: "primary_destructive",
        description: t("agency.modal.deactivate.description"),
        submitLabel: t("modal.label.deactivate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await deactivate(id),
      })
    },
    [deactivate, t],
  )

  const [modalInfo, setModalInfo] = useState<ModalInfo>(undefined)

  const isModalOpen = Boolean(modalInfo)

  const handleCloseSuccess = (_event?: any, reason?: string) => {
    if (reason === "clickaway") return
    setShowSuccess(false)
  }

  const handleCloseError = (_event?: any, reason?: string) => {
    if (reason === "clickaway") return
    setShowError(false)
  }

  const activateErrors =
    [] && errors && errors.fields.map((e: any) => `${e.name}: ${t(e.key)} \n`)

  const isAdmin = hasAuthority(["AUTH_ACCESS_MARKET_ADMIN"])

  const adminTabs = isAdmin ? tabs.filter((tb) => tb !== "documents") : tabs

  return (
    <Container>
      <>
        <Breadcrumbs
          current={agency?.name}
          items={[
            {
              backLink: `${t("nav.admin")} / ${t("nav.market.agencies")}`,
              to: "/market-agencies",
            },
          ]}
        />
        <Header
          label={agency?.name}
          badge={
            <Badge color={ColorMap[agency?.status as any]}>
              {agency?.status}
            </Badge>
          }
          subLabel={<CodeSublabel code={agency?.code} />}
        >
          <MoreActions
            options={[
              // DELETE
              {
                hidden:
                  !(agency?.status === "NEW") ||
                  !hasAuthority(["AUTH_EDIT_AGENCY"]),
                label: t("market-admin.agencies.list.delete.action"),
                icon: <DeleteIcon />,
                onClick: () => {
                  onDelete(id)
                },
              },
              {
                label: t("agency.options.activate"),
                icon: <CheckIcon />,
                hidden:
                  !(
                    agency?.status === "NEW" || agency?.status === "INACTIVE"
                  ) || !hasAuthority(["AUTH_EDIT_AGENCY"]),
                onClick: () => {
                  onActivate(id)
                },
              },
              {
                label: t("deactivate"),
                icon: <Deactivate />,
                hidden:
                  !(agency?.status === "ACTIVE") ||
                  !hasAuthority(["AUTH_EDIT_AGENCY"]),
                onClick: () => {
                  onDeactivate(id)
                },
              },
            ]}
          />
        </Header>
        <Surface mb={2} flexDirection="row" className={styles.wrapper}>
          <div className={styles.main}>
            <Tabs
              value={tab}
              {...styledTabsProps}
              classes={{
                ...styledTabsProps.classes,
                flexContainer: styledTabsProps?.classes?.flexContainer,
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {adminTabs.map((tab, idx) => (
                <TabLink
                  key={idx}
                  to={`/market-agencies/${id}/${tab}`}
                  classes={styledTabClasses}
                  value={tab}
                  label={t(`market.tabs.${tab}`)}
                />
              ))}
            </Tabs>
            <div>
              <Switch>
                <Route path="/market-agencies/:id/profile">
                  <Profile />
                </Route>
                <Route path="/market-agencies/:id/media-types">
                  <Surface className={styles.surface}>
                    <GlobalMediaTypes
                      configOwnerId={id}
                      configScope="MARKET_AGENCY"
                    />
                  </Surface>
                </Route>
                <Route path="/market-agencies/:id/team">
                  <Team />
                </Route>
                <Route path="/market-agencies/:id/companies">
                  <Surface className={styles.surface}>
                    <Companies />
                  </Surface>
                </Route>
                <Route path="/market-agencies/:id/clients">
                  <Surface className={styles.surface}>
                    <Clients />
                  </Surface>
                </Route>
                <Route path="/market-agencies/:id/documents">
                  <Surface className={cx(styles.surface, styles.documents)}>
                    <Documents />
                  </Surface>
                </Route>
                <Route path="/market-agencies/:id/fees">
                  <Surface className={styles.surface}>
                    <Fees
                      queryKeyParam="market-agencies"
                      configScope="AGENCY"
                    />
                  </Surface>
                </Route>
                <Route path="/market-agencies/:id/commissions">
                  <Commissions
                    queryKeyParam="market-agencies"
                    configScope="AGENCY"
                  />
                </Route>
                <Route path="/market-agencies/:id/vendors">
                  <Surface className={styles.surface}>
                    <Vendors />
                  </Surface>
                </Route>
              </Switch>
            </div>
          </div>
        </Surface>
        <ConfirmationModal
          open={isModalOpen}
          onCancel={() => setModalInfo(undefined)}
          {...modalInfo}
        />
        <SnackBar
          open={showSuccess}
          severity="success"
          onClose={handleCloseSuccess}
          message={t("agency.save.success")}
        />
        <SnackBar
          open={showError}
          severity="error"
          onClose={handleCloseError}
          message={errors && activateErrors.join("")}
        />
      </>
    </Container>
  )
}

export default MarketAgencyDetails
